import type { IButtonCompProps } from '@/components';
import { DropdownItemBase } from './DropdownItemBase';
import { reactive, type Ref } from 'vue';

type IDropdownItemButton = {
    [K in keyof IButtonCompProps]: undefined extends IButtonCompProps[K]
        ? IButtonCompProps[K] | Ref<Exclude<IButtonCompProps[K], undefined>> | undefined
        : IButtonCompProps[K] | Ref<IButtonCompProps[K]>;
};

interface IDropdownItemButtonOptions {
    translationkey?: Ref<string> | string;
    subtext?: Ref<string> | string;
    subtextclass?: Ref<string> | string;
    tooltip?: Ref<string> | string;
    shortcut?: string;
    class?: Ref<string> | string;
    hideDropDownItem?: Ref<boolean>;
    markTextPart?: RegExp | string | Ref<RegExp | string | undefined>;
}

export class DropdownItemButton<TAction extends (...args: any[]) => any | Promise<any> = () => any | Promise<any>>
    extends DropdownItemBase
    implements IDropdownItemButtonOptions
{
    props: IButtonCompProps;
    translationkey?: Ref<string> | string;
    subtext?: Ref<string> | string;
    tooltip?: Ref<string> | string;
    shortcut?: string;
    class?: Ref<string> | string;
    subtextclass?: Ref<string> | string;
    hideDropDownItem?: Ref<boolean>;

    get uniqueLoopKey() {
        return this.loopKey ?? `${this.translationkey}-${this.subtext}-${this.tooltip}-${this.shortcut}-${this.class}`;
    }

    private loopKey?: string;

    action: TAction;

    /**
     * This is here so a pojo wont fit the class, and thus we get build errors if we havent used the create method
     */
    private abuseBlocker: boolean = false;

    private constructor(action: TAction, props: IDropdownItemButton, options: IDropdownItemButtonOptions, uniqueLoopKey?: string) {
        super();

        this.action = action;
        this.translationkey = options.translationkey;
        this.subtext = options.subtext;
        this.subtextclass = options.subtextclass;
        this.tooltip = options.tooltip;
        this.shortcut = options.shortcut;
        this.class = options.class;
        this.hideDropDownItem = options.hideDropDownItem;
        this.markTextPart = options.markTextPart;

        this.loopKey = uniqueLoopKey;

        this.props = reactive(props);
    }

    public static create<TAction extends (...args: any[]) => any | Promise<any> = () => any | Promise<any>>(
        options: { action: TAction } & Partial<IDropdownItemButton> &
            Partial<Pick<DropdownItemBase, 'hideDropDownItem' | 'uniqueLoopKey'>> &
            IDropdownItemButtonOptions,
    ): DropdownItemButton<TAction> {
        const { action, markTextPart, translationkey, subtext, subtextclass, tooltip, shortcut, hideDropDownItem, ...props } = options;

        return new DropdownItemButton<TAction>(
            action,
            props,
            {
                translationkey,
                subtext,
                tooltip,
                shortcut,
                hideDropDownItem,
                markTextPart,
                subtextclass,
            },
            options.uniqueLoopKey,
        );
    }
}
